<template>
  <div class="page-card talent-center-page">
    <div class="page-head">
      <h2 class="page-title">才艺中心</h2>
      <div class="page-actions">
        <el-button
          v-if="canUpload"
          type="primary"
          icon="el-icon-plus"
          @click="openUploadDialog"
        >
          发布视频
        </el-button>
      </div>
    </div>

    <div class="toolbar">
      <div class="toolbar-left">
        <el-input
          v-model="query.keyword"
          clearable
          placeholder="标题/艺人/分类"
          style="width: 260px"
          @keyup.enter.native="fetchData"
        />
        <el-select
          v-model="query.shelfStatus"
          clearable
          placeholder="上架状态"
          style="width: 150px"
        >
          <el-option label="已上架" :value="1" />
          <el-option label="已下架" :value="2" />
        </el-select>
        <el-button type="primary" @click="fetchData">查询</el-button>
      </div>
    </div>

    <el-table :data="list" border v-loading="loading">
      <el-table-column label="视频" min-width="220">
        <template slot-scope="{ row }">
          <div class="video-cell">
            <img v-if="fullUrl(row.coverUrl)" class="video-cover" :src="fullUrl(row.coverUrl)" alt="">
            <div v-else class="video-cover video-cover-empty">无封面</div>
            <div class="video-info">
              <div class="video-title">{{ row.title || '-' }}</div>
              <div class="video-sub muted-text">{{ row.taxonomyName || '-' }}</div>
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="来源" min-width="180">
        <template slot-scope="{ row }">
          <div>{{ row.artistName || '平台发布' }}</div>
          <div class="video-sub muted-text">{{ row.artistUserId ? '后台代发' : '后台发布' }}</div>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="120">
        <template slot-scope="{ row }">
          <el-tag :type="row.shelfStatus === 1 ? 'success' : 'info'">
            {{ row.shelfStatus === 1 ? '已上架' : '已下架' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="updatedAt" label="更新时间" width="180" />
      <el-table-column label="操作" width="200" fixed="right">
        <template slot-scope="{ row }">
          <el-button type="text" @click="openPreview(row)">预览</el-button>
          <el-button
            v-if="row.shelfStatus === 1 && canOffline"
            type="text"
            class="danger-link"
            @click="offShelf(row)"
          >
            下架
          </el-button>
          <el-button
            v-else-if="row.shelfStatus !== 1 && canUpload"
            type="text"
            @click="onShelf(row)"
          >
            上架
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog
      title="发布才艺视频"
      :visible.sync="uploadDialogVisible"
      width="760px"
      @close="clearUploadPreview"
    >
      <el-form ref="uploadFormRef" :model="uploadForm" :rules="uploadRules" label-width="110px">
        <el-form-item label="发布方式" prop="publishMode">
          <el-radio-group v-model="uploadForm.publishMode" @change="handlePublishModeChange">
            <el-radio :label="1">后台发布</el-radio>
            <el-radio :label="2">代发上传</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item v-if="uploadForm.publishMode === 2" label="归属艺人" prop="artistUserId">
          <el-select
            v-model="uploadForm.artistUserId"
            filterable
            remote
            reserve-keyword
            placeholder="搜索艺人"
            :remote-method="fetchArtistOptions"
            :loading="artistLoading"
            style="width: 100%"
          >
            <el-option
              v-for="item in artistOptions"
              :key="item.userId"
              :label="artistOptionLabel(item)"
              :value="String(item.userId)"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="标题" prop="title">
          <el-input v-model="uploadForm.title" maxlength="80" show-word-limit />
        </el-form-item>
        <el-form-item label="分类" prop="taxonomyId">
          <el-select v-model="uploadForm.taxonomyId" filterable placeholder="选择分类" style="width: 100%">
            <el-option
              v-for="item in taxonomyOptions"
              :key="item.id"
              :label="item.taxonomyName"
              :value="String(item.id)"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="视频文件" prop="file">
          <el-upload
            action="#"
            :show-file-list="false"
            :auto-upload="false"
            :on-change="handleFileChange"
            :accept="videoAccept"
          >
            <el-button icon="el-icon-upload">选择视频</el-button>
          </el-upload>
          <div class="form-tip">{{ uploadForm.fileName || '支持 mp4/mov/m4v/3gp/avi' }}</div>
          <div v-if="uploadPreviewUrl" class="upload-preview">
            <video
              class="upload-preview-video"
              :src="uploadPreviewUrl"
              controls
              preload="metadata"
            />
          </div>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="closeUploadDialog">取消</el-button>
        <el-button type="primary" :loading="uploading" @click="submitUpload">发布并上架</el-button>
      </span>
    </el-dialog>

    <el-dialog title="视频预览" :visible.sync="previewVisible" width="960px" top="6vh" append-to-body>
      <video
        v-if="preview.videoUrl"
        class="preview-video"
        :src="preview.videoUrl"
        :poster="preview.coverUrl"
        controls
        autoplay
      />
      <span slot="footer">
        <el-button @click="previewVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { MessageBox } from 'element-ui'
import {
  getPlatformTalentVideoArtistOptions,
  getPlatformTalentVideos,
  offShelfPlatformTalentVideo,
  onShelfPlatformTalentVideo,
  uploadPlatformTalentVideo
} from '../../../api/platformManage'
import request, { API_BASE_URL } from '../../../utils/request'
import { hasPermission } from '../../../utils/auth'

const videoAccept = 'video/mp4,video/quicktime,video/x-m4v,video/3gpp,video/x-msvideo'

export default {
  name: 'PlatformTalentVideosPage',
  data() {
    return {
      loading: false,
      uploading: false,
      list: [],
      query: {
        keyword: '',
        shelfStatus: 1
      },
      previewVisible: false,
      preview: {},
      uploadDialogVisible: false,
      artistLoading: false,
      artistOptions: [],
      taxonomyOptions: [],
      videoAccept,
      uploadPreviewUrl: '',
      uploadForm: {
        id: '',
        publishMode: 1,
        artistUserId: '',
        title: '',
        taxonomyId: '',
        file: null,
        fileName: ''
      },
      uploadRules: {
        publishMode: [{ required: true, message: '请选择发布方式', trigger: 'change' }],
        artistUserId: [{
          validator: (rule, value, callback) => {
            if (this.uploadForm.publishMode === 2 && !value) {
              callback(new Error('请选择归属艺人'))
              return
            }
            callback()
          },
          trigger: 'change'
        }],
        title: [{ required: true, message: '请输入标题', trigger: 'blur' }],
        taxonomyId: [{ required: true, message: '请选择分类', trigger: 'change' }]
      }
    }
  },
  computed: {
    canView() {
      return hasPermission('artist_talent_video_manage:view')
    },
    canUpload() {
      return hasPermission('artist_talent_video_manage:upload')
    },
    canOffline() {
      return hasPermission('artist_talent_video_manage:offline')
    }
  },
  created() {
    this.fetchTaxonomies()
    this.fetchData()
  },
  beforeDestroy() {
    this.clearUploadPreview()
  },
  methods: {
    async fetchTaxonomies() {
      const data = await request({
        url: '/api/open/taxonomies?businessType=7&taxonomyType=1',
        method: 'get'
      }).catch(() => [])
      this.taxonomyOptions = Array.isArray(data) ? data : []
    },
    async fetchData() {
      this.loading = true
      try {
        this.list = await getPlatformTalentVideos(this.query).catch(() => [])
      } finally {
        this.loading = false
      }
    },
    openPreview(row) {
      this.preview = {
        videoUrl: this.fullUrl(row.videoUrl),
        coverUrl: this.fullUrl(row.coverUrl)
      }
      this.previewVisible = true
    },
    async offShelf(row) {
      try {
        await MessageBox.confirm(`确认下架视频「${row.title || '-'}」？`, '下架确认', {
          type: 'warning',
          confirmButtonText: '下架',
          cancelButtonText: '取消'
        })
      } catch (e) {
        return
      }
      await offShelfPlatformTalentVideo(row.id)
      this.$message.success('已下架')
      await this.fetchData()
    },
    async onShelf(row) {
      await onShelfPlatformTalentVideo(row.id)
      this.$message.success('已上架')
      await this.fetchData()
    },
    openUploadDialog() {
      if (!this.canUpload) return
      this.clearUploadPreview()
      this.uploadForm = {
        id: '',
        publishMode: 1,
        artistUserId: '',
        title: '',
        taxonomyId: this.taxonomyOptions[0] ? String(this.taxonomyOptions[0].id) : '',
        file: null,
        fileName: ''
      }
      this.uploadDialogVisible = true
    },
    handlePublishModeChange() {
      if (this.uploadForm.publishMode === 1) {
        this.uploadForm.artistUserId = ''
      } else if (!this.uploadForm.artistUserId) {
        this.fetchArtistOptions('')
      }
    },
    async fetchArtistOptions(keyword = '') {
      this.artistLoading = true
      try {
        const data = await getPlatformTalentVideoArtistOptions({ keyword, page: 1, pageSize: 20 }).catch(() => ({ records: [] }))
        const records = Array.isArray(data.records) ? data.records : []
        this.artistOptions = records.map(item => ({
          userId: item.userId,
          artistName: item.artistName,
          nickname: item.nickname,
          subtitle: item.mobile
        }))
      } finally {
        this.artistLoading = false
      }
    },
    artistOptionLabel(item) {
      const name = item.artistName || item.nickname || item.userId || '-'
      return item.subtitle ? `${name} (${item.subtitle})` : name
    },
    handleFileChange(file) {
      this.clearUploadPreview()
      this.uploadForm.file = file.raw
      this.uploadForm.fileName = file.name
      if (file.raw) {
        this.uploadPreviewUrl = URL.createObjectURL(file.raw)
      }
    },
    closeUploadDialog() {
      this.uploadDialogVisible = false
      this.clearUploadPreview()
    },
    clearUploadPreview() {
      if (this.uploadPreviewUrl) {
        URL.revokeObjectURL(this.uploadPreviewUrl)
        this.uploadPreviewUrl = ''
      }
    },
    async submitUpload() {
      this.$refs.uploadFormRef.validate(async valid => {
        if (!valid) return
        if (!this.uploadForm.file) {
          this.$message.warning('请选择视频文件')
          return
        }
        this.uploading = true
        try {
          const formData = new FormData()
          formData.append('title', this.uploadForm.title)
          formData.append('taxonomyId', this.uploadForm.taxonomyId)
          if (this.uploadForm.publishMode === 2 && this.uploadForm.artistUserId) {
            formData.append('artistUserId', this.uploadForm.artistUserId)
          }
          formData.append('file', this.uploadForm.file)
          await uploadPlatformTalentVideo(formData)
          this.$message.success('发布成功，已自动上架')
          this.closeUploadDialog()
          await this.fetchData()
        } finally {
          this.uploading = false
        }
      })
    },
    fullUrl(url) {
      if (!url) return ''
      if (url.startsWith('http')) return url
      if (url.startsWith('/uploads/')) {
        const uploadPath = url.replace('/uploads/', '')
        if (uploadPath.includes('/')) {
          return `${API_BASE_URL}${url}`
        }
        return `${API_BASE_URL}/api/common/file/${uploadPath}`
      }
      return `${API_BASE_URL}${url}`
    }
  }
}
</script>

<style scoped>
.page-head,
.toolbar,
.toolbar-left {
  display: flex;
  align-items: center;
}

.page-head,
.toolbar {
  justify-content: space-between;
}

.toolbar {
  margin-bottom: 16px;
}

.toolbar-left {
  gap: 10px;
  flex-wrap: wrap;
}

.video-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.video-cover {
  width: 62px;
  height: 84px;
  border-radius: 8px;
  object-fit: cover;
  background: #f5f7fa;
  flex-shrink: 0;
}

.video-cover-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #909399;
  font-size: 12px;
}

.video-info {
  min-width: 0;
}

.video-title {
  font-weight: 600;
  color: #303133;
}

.video-sub,
.muted-text,
.form-tip {
  margin-top: 4px;
  color: #909399;
  font-size: 12px;
}

.preview-video {
  width: 100%;
  max-height: 70vh;
  border-radius: 10px;
  background: #111827;
}

.upload-preview {
  margin-top: 12px;
  width: 320px;
  max-width: 100%;
}

.upload-preview-video {
  display: block;
  width: 100%;
  max-height: 220px;
  border-radius: 8px;
  background: #111827;
}

.danger-link {
  color: #f56c6c;
}
</style>
